import * as React from "react";
import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSnackbar } from "notistack";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as LocksState from "../../store/Locks";
import * as SitesState from "../../store/Sites";
import * as SiteUsersState from "../../store/SiteUsers";
import { confirmPrompt } from "../../helpers";
import {
  SiteResponse,
  SiteSystemType,
  ConnectUserResponse,
  AccessControlDoorType,
  InviteResponse,
} from "../../api/ApiClient";
import SelectSite from "../shared/SelectSite";
import Modal from "../shared/Modal";
import SelectClientUser from "../shared/SelectClientUser";

import "./adminSites.css";
import { SiteSyncingState } from "../../store/Sites";
import { CSVLockUploaderModal } from "./CSVLockUploaderModal";
import { SyncPreviewModal } from "./SyncPreviewModal";
import { Button } from "components/ui/button";
import { SITES_VIEW, setSitesViewPreference } from "../../shared/utils/sitesViewPreference";

interface Injected {}
type Props = Injected &
  typeof LocksState.actionCreators &
  typeof SitesState.actionCreators &
  typeof SiteUsersState.actionCreators &
  any;

type ModalType = "user" | "lock" | "csv" | null;

const AdminSites: React.FC<Props> = (props) => {
  // Local state (converted from class state)
  const [inProgress, setInProgress] = useState<Record<string, boolean>>({});
  const [selectedSite, setSelectedSite] = useState<SiteResponse | null>(null);
  const [showingModal, setShowingModal] = useState<ModalType>(null);
  const [previewSite, setPreviewSite] = useState<SiteSyncingState | null>(null);
  const [resultEndpointId, setResultEndpointId] = useState<string>("");
  const [resultInviteCode, setResultInviteCode] = useState<string>("");
  const [credentialId, setCredentialId] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<{
    externalId?: string;
    name?: string;
  } | null>(null);
  const [selectingUser, setSelectingUser] = useState<boolean>(true);
  const [systemUserId, setSystemUserId] = useState<string>("");
  const [siteId, setSiteId] = useState<string>("");
  // lockId is set after registering a lock but not used elsewhere; removed
  const [lockName, setLockName] = useState<string>("");
  const [systemLockId, setSystemLockId] = useState<string>("");
  // Removed unused applicationUserId, phone, endpointId

  const setProgressFlag = useCallback((key: string, value: boolean) => {
    setInProgress((prev) => ({ ...prev, [key]: value }));
  }, []);

  const addLockForSite = useCallback((site: SiteResponse) => {
    setSelectedSite(site);
    setSiteId(site.id || "");
    setShowingModal("csv");
  }, []);

  const syncLocks = useCallback(
    (id: string) => {
      props.siteLockSync({ id });
    },
    [props],
  );

  const openPreview = useCallback((site: SiteSyncingState) => {
    setPreviewSite(site);
  }, []);

  const syncACG = useCallback(
    (id: string) => {
      props.siteACGSync({ id });
    },
    [props],
  );

  const addUserForSite = useCallback((site: SiteResponse) => {
    setSelectedSite(site);
    setSiteId(site.id || "");
    setShowingModal("user");
  }, []);

  const { enqueueSnackbar } = useSnackbar();

  const registerLock = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setProgressFlag("registerLock", true);
      try {
        await props.registerLock({
          siteId: siteId || "",
          systemLockId,
          name: lockName,
          doorType: AccessControlDoorType.Common,
        });
        enqueueSnackbar("Successfully registered lock", { variant: "success" });
        setSystemLockId("");
        await props.requestLocks();
      } catch (e) {
        enqueueSnackbar(
          "Error registering lock: " +
            (e instanceof Error ? e.message : JSON.stringify(e)),
          { variant: "error" },
        );
      } finally {
        setProgressFlag("registerLock", false);
      }
    },
    [props, siteId, systemLockId, lockName, setProgressFlag, enqueueSnackbar],
  );

  const deleteSite = useCallback(
    (site: SiteResponse) => {
      confirmPrompt({
        promptMessage: "Are you sure you want to delete this site?",
        onConfirm: async () => {
          try {
            await props.deleteSite({ id: site.id || "" });
            await props.requestSites();
            enqueueSnackbar("Site deleted", { variant: "success" });
          } catch (e) {
            enqueueSnackbar(
              "Error deleting site: " +
                (e instanceof Error ? e.message : JSON.stringify(e)),
              { variant: "error" },
            );
          }
        },
      });
    },
    [props, enqueueSnackbar],
  );

  const inviteAssaUser = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setProgressFlag("inviteAssaUser", true);
      try {
        const json: InviteResponse = (await props.inviteAssaUser({
          siteId: siteId || "",
          phone: "",
          clientUserId: selectedUser?.externalId || "",
        })) as any;
        setResultEndpointId(json.endpointId || "");
        setResultInviteCode(json.invitationCode || "");
        enqueueSnackbar("Invited Assa Abloy user", { variant: "success" });
        await props.requestSiteUsers();
      } catch (e) {
        enqueueSnackbar(
          "Error inviting Assa user: " +
            (e instanceof Error ? e.message : JSON.stringify(e)),
          { variant: "error" },
        );
      } finally {
        setProgressFlag("inviteAssaUser", false);
      }
    },
    [props, siteId, selectedUser, setProgressFlag, enqueueSnackbar],
  );

  const registerOpenPathUser = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setProgressFlag("openPathUser", true);
      try {
        await props.registerOpenPathUser({
          siteId: siteId || "",
          credentialId,
          systemUserId,
          clientUserId: selectedUser?.externalId || "",
        });
        await props.requestSiteUsers();
        setCredentialId("");
        setSystemUserId("");
        enqueueSnackbar("Registered OpenPath user", { variant: "success" });
      } catch (e) {
        enqueueSnackbar(
          "Error registering OpenPath User: " +
            (e instanceof Error ? e.message : JSON.stringify(e)),
          { variant: "error" },
        );
      } finally {
        setProgressFlag("openPathUser", false);
      }
    },
    [
      props,
      siteId,
      credentialId,
      systemUserId,
      selectedUser,
      setProgressFlag,
      enqueueSnackbar,
    ],
  );

  const registerSiteUser = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setProgressFlag("openPathUser", true);
      try {
        await props.registerSiteUser({
          siteId: siteId || "",
          credentialId,
          systemUserId,
          clientUserId: selectedUser?.externalId || "",
        });
        await props.requestSiteUsers();
        setCredentialId("");
        setSystemUserId("");
        enqueueSnackbar("Registered site user", { variant: "success" });
      } catch (e) {
        enqueueSnackbar(
          "Error registering site User: " +
            (e instanceof Error ? e.message : JSON.stringify(e)),
          { variant: "error" },
        );
      } finally {
        setProgressFlag("openPathUser", false);
      }
    },
    [
      props,
      siteId,
      credentialId,
      systemUserId,
      selectedUser,
      setProgressFlag,
      enqueueSnackbar,
    ],
  );

  const registerButterflyUser = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setProgressFlag("butterflyUser", true);
      try {
        await props.registerButterflyUser({
          siteId: siteId || "",
          systemUserId,
          clientUserId: selectedUser?.externalId || "",
        });
        await props.requestSiteUsers();
        setSystemUserId("");
        enqueueSnackbar("Registered Butterfly user", { variant: "success" });
      } catch (e) {
        enqueueSnackbar(
          "Error registering Butterfly User: " +
            (e instanceof Error ? e.message : JSON.stringify(e)),
          { variant: "error" },
        );
      } finally {
        setProgressFlag("butterflyUser", false);
      }
    },
    [
      props,
      siteId,
      systemUserId,
      selectedUser,
      setProgressFlag,
      enqueueSnackbar,
    ],
  );

  const closeModal = useCallback(() => setShowingModal(null), []);

  const renderSiteButton = useCallback(
    (site: SiteSyncingState) => {
      return (
        <div className="flex flex-col justify-between gap-2">
          <Button
            onClick={() => deleteSite(site)}
            type="button"
            variant="destructive"
          >
            Delete
          </Button>
          <Button
            onClick={() => addUserForSite(site)}
            type="button"
          >
            Add User
          </Button>
          <React.Fragment>
            <React.Fragment>
              <Button
                className="w-full"
                disabled={site.syncInProgress}
                onClick={() => syncLocks(site.id)}
                type="button"
              >
                Sync Locks
              </Button>
              <Button
                className="w-full"
                disabled={site.syncInProgress}
                onClick={() => openPreview(site)}
                type="button"
                variant="outline"
              >
                Preview Sync
              </Button>
            </React.Fragment>
            {site &&
            site.account &&
            site.account.systemType &&
            [
              SiteSystemType.SaltoSpace,
              SiteSystemType.Assa,
              SiteSystemType.Butterfly,
              SiteSystemType.DormaKaba,
            ].includes(site.account.systemType) ? (
              <Button type="button" onClick={() => addLockForSite(site)}>
                CSV Lock Import
              </Button>
            ) : (
              <></>
            )}
          </React.Fragment>
          <React.Fragment>
            {site &&
              site.account &&
              site.account.systemType === SiteSystemType.SchlageEngage &&
              site.propertyExternalId && (
                <Button
                  disabled={site.syncInProgress}
                  className="w-full bg-gray-200 text-gray-800 hover:bg-gray-300 normal-case shadow-none"
                  onClick={() => syncACG(site.id)}
                  style={{ marginBottom: "5px" }}
                  type="button"
                >
                  Map Locks
                </Button>
              )}
          </React.Fragment>
          {site.syncMessage && (
            <div
              className={`rounded border px-3 py-2 text-sm flex items-start gap-2 ${
                site.syncSuccess
                  ? "bg-green-50 border-green-300 text-green-800"
                  : "bg-red-50 border-red-300 text-red-800"
              }`}
              role="status"
            >
              <span className="flex-1 leading-snug">{site.syncMessage}</span>
            </div>
          )}
        </div>
      );
    },
    [addUserForSite, addLockForSite, deleteSite, syncLocks, syncACG, openPreview],
  );

  const renderSitesTable = useCallback(() => {
    return (
      //@ts-ignore
      <SelectSite
        pageSize={20}
        renderButton={renderSiteButton}
        unselectedLabel="Delete"
        unselectedBtnClass="bg-red-600 hover:bg-red-700 text-white font-medium px-3 py-1 rounded"
      />
    );
  }, [renderSiteButton]);

  const renderSelectUserButton = useCallback(
    (clientUser: ConnectUserResponse) => (
      <div>
        <Button
          onClick={() => {
            setSelectingUser(false);
            setSelectedUser(clientUser);
          }}
          type="button"
        >
          Select
        </Button>
        <p />
      </div>
    ),
    [],
  );

  const renderRegisterAssaUser = useCallback(() => {
    const inviteAssaUserInProgress = (inProgress || {}).inviteAssaUser;
    return (
      <div className="space-y-6">
        <h5>Invite Assa User - Endpoint and Invitation Code</h5>
        <hr />
        <form>
          <div className="mb-4">
            <label htmlFor="clientUserId">User</label>
            <input
              className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              name="clientUserId"
              placeholder="user id"
              value={selectedUser ? selectedUser.name : "please select a user"}
              disabled
              style={{ fontSize: 23 }}
              onChange={() => {}}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="siteId">Site Id *</label>
            <input
              className="block w-full rounded border border-gray-300 bg-gray-50 px-3 py-2 shadow-sm"
              name="site id"
              placeholder="siteId"
              disabled
              value={selectedSite ? selectedSite.id : ""}
              onChange={() => {}}
            />
          </div>
          <div>
            <Button
              type="submit"
              disabled={!!inviteAssaUserInProgress}
              onClick={inviteAssaUser}
            >
              Invite Assa User
            </Button>
          </div>
          <div>Got Endpoint Id: {resultEndpointId} </div>
          <div>Got Invite Code: {resultInviteCode} </div>
        </form>
      </div>
    );
  }, [
    inProgress,
    inviteAssaUser,
    resultEndpointId,
    resultInviteCode,
    selectedSite,
    selectedUser,
  ]);

  const renderRegisterButterflyUser = useCallback(() => {
    const butterflyUserInProgress = (inProgress || {}).butterflyUser;
    return (
      <div className="space-y-6">
        <h5>Register Butterfly User</h5>
        <hr />
        <form>
          <div className="mb-4">
            <label htmlFor="clientUserId">User</label>
            <input
              className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              name="clientUserId"
              placeholder="user id"
              value={selectedUser ? selectedUser.name : "please select a user"}
              disabled
              style={{ fontSize: 23 }}
              onChange={() => {}}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="systemUserId">System User Id *</label>
            <input
              className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              name="systemUserId"
              placeholder="system user id"
              value={systemUserId}
              onChange={(e) => setSystemUserId(e.target.value)}
            />
            <p className="mt-1 text-sm text-gray-500">
              (Try 7011 or 7012 - Butterfly user id)
            </p>
          </div>
          <div className="mb-4">
            <label htmlFor="siteId">Site Id *</label>
            <input
              className="block w-full rounded border border-gray-300 bg-gray-50 px-3 py-2 shadow-sm"
              name="site id"
              placeholder="siteId"
              disabled
              value={selectedSite ? selectedSite.id : ""}
              onChange={() => {}}
            />
          </div>
          <div>
            <Button
              type="submit"
              disabled={!!butterflyUserInProgress}
              onClick={registerButterflyUser}
            >
              Register Butterfly User
            </Button>
          </div>
        </form>
      </div>
    );
  }, [
    inProgress,
    registerButterflyUser,
    selectedSite,
    selectedUser,
    systemUserId,
  ]);

  const renderRegisterOpenPathUser = useCallback(() => {
    const openPathUserInProgress = (inProgress || {}).openPathUser;
    return (
      <div className="space-y-6">
        <h5>Register OpenPath User - map to OpenPath IDs</h5>
        <hr />
        <form>
          <div className="mb-4">
            <label htmlFor="clientUserId">User</label>
            <input
              className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              name="clientUserId"
              placeholder="user id"
              value={selectedUser ? selectedUser.name : "please select a user"}
              disabled
              style={{ fontSize: 23 }}
              onChange={() => {}}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="systemUserId">System User Id *</label>
            <input
              className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              name="systemUserId"
              placeholder="system user id"
              value={systemUserId}
              onChange={(e) => setSystemUserId(e.target.value)}
            />
            <p className="mt-1 text-sm text-gray-500">
              (Try 18965 - OpenPath user id)
            </p>
          </div>
          <div className="mb-4">
            <label htmlFor="credentialId">CredentialId *</label>
            <input
              className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              name="credentialId"
              placeholder="OpenPath credential id"
              value={credentialId}
              onChange={(e) => setCredentialId(e.target.value)}
            />
            <p className="mt-1 text-sm text-gray-500">
              (Try 5169 - OpenPath credential id)
            </p>
          </div>
          <div className="mb-4">
            <label htmlFor="siteId">Site Id *</label>
            <input
              className="block w-full rounded border border-gray-300 bg-gray-50 px-3 py-2 shadow-sm"
              name="site id"
              placeholder="siteId"
              disabled
              value={selectedSite ? selectedSite.id : ""}
              onChange={() => {}}
            />
          </div>
          <div>
            <Button
              type="submit"
              disabled={!!openPathUserInProgress}
              onClick={registerOpenPathUser}
            >
              Register OpenPath User
            </Button>
          </div>
        </form>
      </div>
    );
  }, [
    credentialId,
    inProgress,
    registerOpenPathUser,
    selectedSite,
    selectedUser,
    systemUserId,
  ]);

  const renderRegisterSiteUser = useCallback(() => {
    const openPathUserInProgress = (inProgress || {}).openPathUser;
    return (
      <div className="space-y-6">
        <h5>Register Site User - map to System IDs</h5>
        <hr />
        <form>
          <div className="mb-4">
            <label htmlFor="clientUserId">User</label>
            <input
              className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              name="clientUserId"
              placeholder="user id"
              value={selectedUser ? selectedUser.name : "please select a user"}
              disabled
              style={{ fontSize: 23 }}
              onChange={() => {}}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="systemUserId">System User Id *</label>
            <input
              className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              name="systemUserId"
              placeholder="system user id"
              value={systemUserId}
              onChange={(e) => setSystemUserId(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="credentialId">CredentialId</label>
            <input
              className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              name="credentialId"
              placeholder="optional additional credential id"
              value={credentialId}
              onChange={(e) => setCredentialId(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="siteId">Site Id *</label>
            <input
              className="block w-full rounded border border-gray-300 bg-gray-50 px-3 py-2 shadow-sm"
              name="site id"
              placeholder="siteId"
              disabled
              value={selectedSite ? selectedSite.id : ""}
              onChange={() => {}}
            />
          </div>
          <div>
            <Button
              type="submit"
              disabled={!!openPathUserInProgress}
              onClick={registerSiteUser}
            >
              Register Site User
            </Button>
          </div>
        </form>
      </div>
    );
  }, [
    credentialId,
    inProgress,
    registerSiteUser,
    selectedSite,
    selectedUser,
    systemUserId,
  ]);

  const renderRegisterUser = useCallback(() => {
    if (!selectedSite || !selectedSite.account) return null;
    switch (selectedSite.account.systemType) {
      case SiteSystemType.Assa:
        return renderRegisterAssaUser();
      case SiteSystemType.Openpath:
        return renderRegisterOpenPathUser();
      case SiteSystemType.Butterfly:
        return renderRegisterButterflyUser();
      case SiteSystemType.Stratis:
        return renderRegisterSiteUser();
      default:
        return <div>Adding a user not yet supported</div>;
    }
  }, [
    renderRegisterAssaUser,
    renderRegisterOpenPathUser,
    renderRegisterButterflyUser,
    renderRegisterSiteUser,
    selectedSite,
  ]);

  const renderRegisterUserWrapper = useCallback(() => {
    return (
      <div className="space-y-6">
        {(!selectedUser || selectingUser) && (
          <>
            <h3>Please Select a User</h3>
            <Button
              className="bg-gray-200 text-gray-800 hover:bg-gray-300 normal-case shadow-none"
              onClick={() =>
                selectedUser?.externalId && setSelectingUser(false)
              }
              type="button"
            >
              Done selecting user
            </Button>
            <p />
            <div className="mb-4">
              <label htmlFor="clientUserId">Email</label>
              <input
                className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                name="clientUserId"
                placeholder="email"
                value={selectedUser ? selectedUser.externalId : ""}
                style={{ fontSize: 23 }}
                onChange={(e) =>
                  setSelectedUser({
                    externalId: e.target.value,
                    name: e.target.value,
                  })
                }
              />
              <p className="mt-1 text-sm text-gray-500">
                Can use this for dashboard @livly.io users
              </p>
            </div>
            <h3>Or</h3>
            {
              //@ts-ignore
              <SelectClientUser
                pageSize={5}
                renderButton={renderSelectUserButton}
              />
            }
          </>
        )}
        {!selectingUser && (
          <>
            <Button
              className="bg-gray-200 text-gray-800 hover:bg-gray-300 normal-case shadow-none"
              onClick={() => {
                setSelectingUser(true);
                setSelectedUser(null);
              }}
              type="button"
            >
              Change User
            </Button>
            {renderRegisterUser()}
          </>
        )}
      </div>
    );
  }, [renderSelectUserButton, renderRegisterUser, selectedUser, selectingUser]);

  const renderCsvUpload = useCallback(() => {
    const registerLockInProgress = (inProgress || {}).registerLock;
    return (
      <CSVLockUploaderModal
        isDisabled={!!registerLockInProgress}
        siteId={String(siteId)}
        onHide={closeModal}
        systemType={selectedSite?.account?.systemType}
      />
    );
  }, [closeModal, inProgress, siteId, selectedSite]);

  const renderRegisterLock = useCallback(() => {
    const registerLockInProgress = (inProgress || {}).registerLock;
    return (
      <form className="space-y-4">
        <div>
          <label htmlFor="lockName">Lock Name *</label>
          <input
            className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            name="lockName"
            placeholder="lock name"
            value={lockName}
            onChange={(e) => setLockName(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="siteId">Site Id *</label>
          <input
            className="block w-full rounded border border-gray-300 bg-gray-50 px-3 py-2 shadow-sm"
            name="site id"
            placeholder="siteId"
            disabled
            value={selectedSite ? selectedSite.id : ""}
            onChange={() => {}}
          />
        </div>
        <div>
          <label htmlFor="systemLockId">System Lock Id *</label>
          <input
            className="block w-full rounded border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            name="systemLockId"
            placeholder="system lock id. e.g. CL0602"
            value={systemLockId}
            onChange={(e) => setSystemLockId(e.target.value)}
          />
          <p className="mt-1 text-sm text-gray-500">
            Try 101 for Assa, 215 for OpenPath, 3579 for Butterfly, "n/a" for
            Buzzly, Stratis or LivlySms
          </p>
        </div>
        <div>
          <Button
            type="submit"
            disabled={!!registerLockInProgress}
            onClick={registerLock}
          >
            Register Lock
          </Button>
        </div>
      </form>
    );
  }, [inProgress, lockName, registerLock, selectedSite, systemLockId]);

  return (
    <main className="space-y-6">
      <header className="pb-5 border-b border-slate-200 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7 text-slate-900">
              Sites
          </h1>
          <Link
            data-testid="sites-link-switch-view"
            to="/admin/sites"
            onClick={() => setSitesViewPreference(SITES_VIEW.NEW)}
            className="inline-block mt-1 text-sm underline text-slate-500 underline-offset-2 hover:text-slate-700"
          >
            Switch to new view
          </Link>
        </div>
      </header>
      {renderSitesTable()}
      <Modal
        show={!!showingModal}
        onHide={closeModal}
        aria-labelledby="ModalHeader"
        dialogClassName={showingModal !== "csv" && "custom-modal"}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title id="ModalHeader">
            {showingModal === "user"
              ? "Add User to Site"
              : showingModal === "csv"
                ? "CSV Lock Import"
                : "Add Lock"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showingModal === "user"
            ? renderRegisterUserWrapper()
            : showingModal === "csv"
              ? renderCsvUpload()
              : renderRegisterLock()}
        </Modal.Body>
      </Modal>
      {previewSite && (
        <SyncPreviewModal
          siteId={previewSite.id}
          siteName={previewSite.name ?? ""}
          syncFlags={[1]}
          onClose={() => setPreviewSite(null)}
          onRunSync={() => syncLocks(previewSite.id)}
        />
      )}
    </main>
  );
};

export default connect<any, any, any, ApplicationState>(
  (state) => ({
    sites: state.sites,
  }),
  {
    ...LocksState.actionCreators,
    ...SitesState.actionCreators,
    ...SiteUsersState.actionCreators,
  },
)(AdminSites);
